import { Injectable } from '@angular/core';

/**
 * Serialisable UI state persisted per list-route.
 *
 * Only plain scalar / plain-object fields are allowed to ensure
 * JSON round-trip safety (NFR: lightweight serialisation).
 */
export interface RouteState {
    scroll?: number;
    filters?: Record<string, unknown>;
    search?: string;
    sort?: string;
    pagination?: number;
}

/**
 * StatePersistenceService
 *
 * Persists and restores serialisable route state in sessionStorage.
 *
 * Key format: `rrs_v1_<btoa(encodeURIComponent(path))>`
 *   - `rrs`  → namespace prefix
 *   - `v1`   → schema version (bump when RouteState shape changes)
 *   - encoded → collision-free path encoding
 *
 * State restore priority (per the specification):
 *   1. RouteReuseStrategy in-memory cache  (handled by strategy + cache manager)
 *   2. Persisted storage state             ← this service
 *   3. Fresh initialisation
 *
 * All public methods are silent on sessionStorage errors (quota exceeded,
 * private-browsing restrictions, SSR environment).
 */
@Injectable({ providedIn: 'root' })
export class StatePersistenceService {
    private readonly SCHEMA_VERSION = 'v1';
    private readonly KEY_PREFIX = 'rrs';

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Serialises and persists `state` for the given `path`.
     * Silently no-ops if sessionStorage is unavailable or quota is exceeded.
     */
    persist(path: string, state: RouteState): void {
        try {
            sessionStorage.setItem(this.buildKey(path), JSON.stringify(state));
        } catch {
            // sessionStorage may throw in private-browsing or when quota is exceeded.
        }
    }

    /**
     * Retrieves and deserialises persisted state for `path`.
     * Returns null if no state is found or deserialisation fails.
     */
    restore(path: string): RouteState | null {
        try {
            const raw = sessionStorage.getItem(this.buildKey(path));
            if (!raw) {
                return null;
            }
            return JSON.parse(raw) as RouteState;
        } catch {
            return null;
        }
    }

    /**
     * Removes the persisted state for a specific `path`, or clears all
     * `rrs_*` keys from sessionStorage when called without arguments.
     */
    clear(path?: string): void {
        try {
            if (path !== undefined) {
                sessionStorage.removeItem(this.buildKey(path));
            } else {
                this.clearAll();
            }
        } catch {
            // ignore
        }
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private buildKey(path: string): string {
        const encoded = btoa(encodeURIComponent(path));
        return `${this.KEY_PREFIX}_${this.SCHEMA_VERSION}_${encoded}`;
    }

    private clearAll(): void {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(`${this.KEY_PREFIX}_`)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
    }
}
